export class TimeUtilities {
  private static readonly secondsInOneHour = 3600;
  private static readonly secondsInOneMinute = 60;

  private static readonly validTimeFormatRegex =
    /^([0-9]{1,3}:)?([0-5][0-9]):([0-5][0-9])$/;

  private static readonly validInputTimeFormatRegexes = [
    /^(00:)?(00:)?([0-9]{1,2})$/, // when only seconds are provided, allow seconds > 59
    /^(00:)?([0-9]{1,2}):([0-5][0-9])$/, // when only minutes and seconds are provided, allow minutes > 59 but seconds < 59
    /^([0-9]{1,3}):([0-5][0-9]):([0-5][0-9])$/, // when all hours, minutes and seconds are provided, allow hours > 24 but minutes < 59 & seconds < 59
  ];

  static isValidTime(time: string) {
    return this.validTimeFormatRegex.test(time);
  }

  static isValidInputTime(time: string) {
    return this.validInputTimeFormatRegexes.some((r) => r.test(time));
  }

  /**
   * Convert seconds to an HH:MM:SS time string.
   * @param input Number of seconds
   * @returns A string that conforms to the regex `TimeUtilities.validTimeFormatRegex`
   */
  static convertSecondsToTimeString(input: number | undefined): string {
    if (input !== undefined && input !== null) {
      input = Math.floor(input);

      const hours = Math.floor(input / 3600);
      if (hours > 0) {
        input = input - hours * 3600;
      }

      const mins = Math.floor(input / 60);
      if (mins > 0) {
        input = input - mins * 60;
      }
      const secs = input;

      let returnStr = '';
      if (hours > 0) {
        returnStr = hours.toString() + ':';
      }
      returnStr +=
        TimeUtilities.pad(2, mins.toString(), '0') +
        ':' +
        TimeUtilities.pad(2, secs.toString(), '0');
      return returnStr;
    }
    return '';
  }

  /**
   * Converts a HH:MM:SS time string to seconds
   * @param timeString A time string that follows the `TimeUtilities.validTimeFormatRegex`
   * @returns Number of seconds
   */
  static convertTimeStringToSeconds(timeString: string) {
    return this.convertTimeStringToSecondsHelper(
      timeString,
      this.validTimeFormatRegex
    );
  }

  /**
   * Converts a HH:MM:SS time string to seconds.
   * @param timeString A time string that conforms to any regex in `TimeUtilities.validInputTimeFormatRegexes`
   * @returns Number of seconds
   */
  static convertInputTimeStringToSeconds(timeString: string) {
    return this.validInputTimeFormatRegexes
      .map((r) => this.convertTimeStringToSecondsHelper(timeString, r))
      .find((s) => s !== undefined);
  }

  private static convertTimeStringToSecondsHelper(
    timeString: string,
    regex: RegExp
  ): number | undefined {
    const matches = timeString.match(regex);
    if (!matches) return undefined;

    const hoursStr = matches.at(1);
    const minutesStr = matches.at(2);
    const secondsStr = matches.at(3);

    const hours = parseInt(hoursStr || '0');
    const minutes = parseInt(minutesStr || '0');
    const seconds = parseInt(secondsStr || '0');

    const totalDurationSeconds =
      hours * TimeUtilities.secondsInOneHour +
      minutes * this.secondsInOneMinute +
      seconds;

    return totalDurationSeconds;
  }

  private static pad(
    width: number,
    str: string,
    padding: string
  ): string | undefined {
    return width <= str.length
      ? str
      : TimeUtilities.pad(width, padding + str, padding);
  }
}
